import type { ConsultationVitals, ConsultationVitalsInput } from "@/core/clinical/types";
import {
  buildConsultationVitals,
  formatVitalsSummary,
  normalizeConsultationVitals,
} from "@/core/clinical/types";

export type VitalSignContext =
  | "admission"
  | "emergency"
  | "ward_round"
  | "pre_op"
  | "consultation"
  | "other";

export type VitalSignStatus = "recorded" | "validated";

export interface VitalSignRecord {
  id: string;
  patientId: string;
  recordedById: string;
  recordedByName: string;
  context: VitalSignContext;
  admissionId: string | null;
  consultationId: string | null;
  stayId: string | null;
  vitals: ConsultationVitals;
  status: VitalSignStatus;
  recordedAt: string;
  validatedAt: string | null;
  notes: string;
}

export interface VitalSignRecordInput {
  patientId: string;
  recordedById: string;
  recordedByName: string;
  context: VitalSignContext;
  admissionId?: string | null;
  consultationId?: string | null;
  stayId?: string | null;
  vitals: ConsultationVitalsInput;
  notes?: string;
}

export const VITAL_SIGN_CONTEXT_LABELS: Record<VitalSignContext, string> = {
  admission: "Admission",
  emergency: "Urgences",
  ward_round: "Tournée",
  pre_op: "Pré-opératoire",
  consultation: "Consultation",
  other: "Autre",
};

export const VITAL_SIGN_STATUS_LABELS: Record<VitalSignStatus, string> = {
  recorded: "Enregistré",
  validated: "Validé",
};

export function normalizeVitalSignRecord(raw: Record<string, unknown>): VitalSignRecord | null {
  const vitals = normalizeConsultationVitals(raw.vitals);
  if (!vitals) return null;
  return {
    id: String(raw.id ?? ""),
    patientId: String(raw.patientId ?? ""),
    recordedById: String(raw.recordedById ?? ""),
    recordedByName: String(raw.recordedByName ?? ""),
    context: (raw.context as VitalSignContext) ?? "other",
    admissionId: raw.admissionId ? String(raw.admissionId) : null,
    consultationId: raw.consultationId ? String(raw.consultationId) : null,
    stayId: raw.stayId ? String(raw.stayId) : null,
    vitals,
    status: (raw.status as VitalSignStatus) ?? "recorded",
    recordedAt: String(raw.recordedAt ?? new Date().toISOString()),
    validatedAt: raw.validatedAt ? String(raw.validatedAt) : null,
    notes: String(raw.notes ?? ""),
  };
}

export function formatVitalSignRecordSummary(record: VitalSignRecord): string {
  const ctx = VITAL_SIGN_CONTEXT_LABELS[record.context];
  const vitals = formatVitalsSummary(record.vitals);
  return vitals ? `${ctx} · ${vitals}` : ctx;
}

export { buildConsultationVitals };
